import * as React from 'react';
import { Editable, EditablePreview, EditableInput, FormControl, FormLabel, ResponsiveValue } from '@chakra-ui/react';
import { useState } from 'react';
import { toFormattedAmount, toFormattedFixedDecimalAmount, toNumericAmount } from './utils/amountFormatter';

export interface CurrencyAmountFieldProps {
  id: string;
  label: string;
  isInvalid?: boolean;
  isDisabled?: boolean;
  value: number;
  maxWidth?: ResponsiveValue<string | number>;
  placeholder?: string;
  decimal?: number;
  onChange: (value: number) => void;
  ariaDescribeBy?: string;
}

export const CurrencyAmountField = React.memo((props: CurrencyAmountFieldProps) => {
  const { id, label, isInvalid, isDisabled, ariaDescribeBy } = props;
  const { value, onChange, decimal, maxWidth, placeholder = '0' } = props;
  const [dirty, setDirty] = useState(false);
  const [cursor, setCursor] = useState(0);
  const [stateValue, setStateValue] = useState(toFormattedAmount(value.toString(), decimal));
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (dirty && inputRef && inputRef.current) {
      inputRef.current.setSelectionRange(cursor, cursor);
    }
  }, [stateValue, cursor, inputRef, dirty]);

  React.useEffect(() => {
    if (typeof value === 'number') {
      setStateValue(toFormattedAmount(value.toString(), decimal));
    }
  }, [value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value, selectionStart },
    } = event;
    setDirty(true);
    const formattedValue = toFormattedAmount(value, decimal);
    if (selectionStart) {
      // prevent cursor jumping if editing value
      const newCursor = selectionStart + (formattedValue.length - value.length) ?? 1;
      setCursor(newCursor);
    }
    const rawValue = toNumericAmount(formattedValue);
    onChange(rawValue);
    setStateValue(formattedValue);
  };

  const onBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;
    const formattedValue = toFormattedFixedDecimalAmount(value, decimal);
    setStateValue(formattedValue);
  };

  /**
   * EditableInput does not have access to chakra form control to automatically set those tags
   */
  const invalidAriaTags = { 'aria-invalid': isInvalid, 'aria-describedby': ariaDescribeBy };

  return (
    <FormControl id={id} isInvalid={isInvalid}>
      <FormLabel mb={0} fontSize={'md'} fontWeight={'normal'} color={'gray.750'}>
        {label}
      </FormLabel>
      <Editable placeholder={placeholder} value={stateValue} isDisabled={isDisabled} selectAllOnFocus={false}>
        <EditablePreview
          fontSize={{ base: 'xl', md: '3xl' }}
          maxWidth={maxWidth}
          fontWeight={'semibold'}
          color={'text.base'}
          whiteSpace={'nowrap'}
          display={'block'}
          overflow={'hidden'}
          textOverflow={'ellipsis'}
        />
        <EditableInput
          fontSize={{ base: 'xl', md: '3xl' }}
          color={'text.base'}
          fontWeight={'semibold'}
          maxWidth={maxWidth}
          ref={inputRef}
          onChange={handleChange}
          onBlur={onBlur}
          _placeholder={{ color: 'text.base' }}
          {...invalidAriaTags}
        />
      </Editable>
    </FormControl>
  );
});
